"use client";

import { useCallback, useMemo, type MouseEvent } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import useNavItems from "@/app/hooks/nav-items.hook";
import {
    getActiveNavLogoUrl,
    hasActiveProductNavLogo,
    isProductLogoReloadPath,
} from "@/app/utils/get-nav-logo-url";
import { DirectPaths } from "@/config/routes";
import { useAuth } from "@/context/authcontext";
import { useNavMobileTheme, useNavTheme } from "./nav-theme-context";

type NavBrandLogoProps = {
    active?: string;
    className?: string;
    /** Use mobile product logo (e.g. broking mobile mark) when available. */
    forMobile?: boolean;
    /** When false, render a non-interactive mark (e.g. footer). Default true. */
    navigable?: boolean;
    /** Intrinsic image width (Next/Image). Defaults to header mark size. */
    width?: number;
    /** Intrinsic image height (Next/Image). Defaults to header mark size. */
    height?: number;
};

const toWhiteLogoUrl = (url: string) => {
    const original = url.substring(url.lastIndexOf("/"));
    return url.replace(original, "/white" + original);
};

const NavBrandLogo = ({
    active = "",
    className,
    forMobile = false,
    navigable = true,
    width = 119,
    height = 42,
}: NavBrandLogoProps) => {
    const pathname = usePathname();
    const desktopTheme = useNavTheme();
    const mobileTheme = useNavMobileTheme();
    const theme = forMobile ? mobileTheme : desktopTheme;
    const { isAuthenticated } = useAuth();
    const navItems = useNavItems({ active, isLoggedIn: Boolean(isAuthenticated) });

    const isHomePage = pathname === DirectPaths.HOME;
    /** Pre-login: nav active+logoUrl signal (unchanged). */
    const isPreLoginProductLogoPage = useMemo(
        () => hasActiveProductNavLogo(navItems),
        [navItems],
    );
    /**
     * Post-login: pathname-based so Silver reloads the Silver URL even when
     * the bullion layout passes Gold as `active`.
     */
    const isPostLoginProductReloadPage = useMemo(
        () => isProductLogoReloadPath(pathname),
        [pathname],
    );

    const logoUrl = useMemo(() => {
        const defaultLogo = process.env.ICM_LOGO_URL ?? "";
        const useWhiteTransform = theme === "blue";
        return getActiveNavLogoUrl(
            navItems,
            useWhiteTransform ? toWhiteLogoUrl(defaultLogo) : defaultLogo,
            useWhiteTransform ? toWhiteLogoUrl : undefined,
            forMobile,
        );
    }, [navItems, theme, forMobile]);

    /**
     * Pre-login:
     * - Home → no navigation (href is decorative)
     * - Product logo pages → hard reload on click
     * - About / contact / etc. → Home
     *
     * Post-login:
     * - Stocks / Global / Gold / Silver / FD → hard reload current URL
     * - Home / About / Portfolio / etc. → Platform
     */
    const logoHref = useMemo(() => {
        if (!isAuthenticated) {
            if (isHomePage || isPreLoginProductLogoPage) {
                return pathname || DirectPaths.HOME;
            }
            return DirectPaths.HOME;
        }

        if (isPostLoginProductReloadPage) {
            return pathname || DirectPaths.PLATFORM;
        }
        return DirectPaths.PLATFORM;
    }, [
        isAuthenticated,
        isHomePage,
        isPreLoginProductLogoPage,
        isPostLoginProductReloadPage,
        pathname,
    ]);

    const handleLogoClick = useCallback(
        (event: MouseEvent<HTMLAnchorElement>) => {
            if (!isAuthenticated) {
                if (isHomePage) {
                    event.preventDefault();
                    return;
                }

                if (isPreLoginProductLogoPage) {
                    event.preventDefault();
                    window.location.reload();
                }
                return;
            }

            if (isPostLoginProductReloadPage) {
                event.preventDefault();
                window.location.reload();
            }
        },
        [
            isAuthenticated,
            isHomePage,
            isPreLoginProductLogoPage,
            isPostLoginProductReloadPage,
        ],
    );

    const logoImage = (
        <Image
            src={logoUrl}
            alt="InCred Logo"
            width={width}
            height={height}
            className={className ?? "h-[42px] w-auto shrink-0"}
        />
    );

    if (!navigable) {
        return logoImage;
    }

    return (
        <Link
            href={logoHref}
            aria-label="InCred Money home"
            className="cursor-default"
            onClick={handleLogoClick}
        >
            {logoImage}
        </Link>
    );
};

export default NavBrandLogo;
